<template>
  <div class="table-editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <img
          :src="moduleConfigs.icon"
          alt="" />
        <div>{{ moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="layout-label"
        class="editor-section">
        <h2>layout label</h2>
        <div class="body">
          <b-field
            label="Title"
            class="input-field"
            message="">
            <b-input
              v-model="module.title" />
          </b-field>
        </div>
      </div>

      <div
        id="time-range"
        class="editor-section">
        <h2>time range</h2>
        <div class="body">
          <TableTimeRange
            v-model="module.default_time_range"
            :report-default-time-range="reportDefaultTimeRange"
            class="editor-inputs" />
        </div>
      </div>

      <div
        id="filters"
        class="editor-section">
        <h2>filters</h2>
        <div class="body">
          <FilterConfigListEditor
            v-model="module.filters"
            class="editor-inputs" />
        </div>
      </div>

      <div
        id="table-styles"
        class="editor-section">
        <h2>table styles</h2>
        <div class="body">
          <TrackerTableStyle
            v-model="module.tracker_table_style"
            class="editor-inputs" />
        </div>
      </div>

      <div
        id="time-dimension"
        class="editor-section">
        <h2>time dimension</h2>
        <div class="body">
          <TrackerTableTimeDimension
            v-model="module.time_dimension"
            class="editor-inputs" />
        </div>
      </div>

      <div
        id="pivot-rows"
        class="editor-section">
        <h2>pivot rows</h2>
        <div class="body">
          <TrackerPivotRows
            v-model="module.tracker_pivot"
            class="editor-inputs" />
        </div>
      </div>

      <div
        id="total-column-headers"
        class="editor-section">
        <h2>Total column header</h2>
        <div class="body">
          <TrackerTotalHeaders
            v-model="module.tracker_total_headers"
            class="editor-inputs" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import { throttle } from 'lodash'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'
  import TrackerTableStyle from '@/components/pdf_report/tracker_table_module/TrackerTableStyle.vue'
  import TrackerTotalHeaders from '@/components/pdf_report/tracker_table_module/TrackerTotalHeaders.vue'
  import TrackerTableTimeDimension from '@/components/pdf_report/tracker_table_module/TrackerTableTimeDimension.vue'
  import TrackerPivotRows from '@/components/pdf_report/tracker_table_module/TrackerPivotRows.vue'
  import FilterConfigListEditor from '@/components/reports/config/modules/common/FilterConfigListEditor'
  import TableTimeRange from '@/components/pdf_report/table_module/table_settings/tabs/TableTimeRange.vue'

  export default {
    name: 'TrackerTableEditorComp',
    components: {
      TrackerTableStyle,
      TrackerTotalHeaders,
      TrackerTableTimeDimension,
      TrackerPivotRows,
      Tabs,
      FilterConfigListEditor,
      TableTimeRange,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      reportDefaultTimeRange: {
        type: Object,
        default: null,
      },
    },

    data () {
      return {
        moduleConfigs: {
          name: 'Tracker Table',
          type: 'TrackerTableModule',
          icon: require('@/assets/icons/tracker-table.svg'),
        },
        inputSectionTab: 'layout-label',
      }
    },

    computed: {
      module: {
        get () {
          return this.value || {}
        },
        set (value) {
          this.$emit('input', value)
        },
      },

      throttleScroll () {
        return throttle(this.onScroll, 100)
      },

      tabs () {
        return [
          {
            label: 'Layout Label',
            id: 'layout-label',
            hasErrors: false,
          },
          {
            label: 'Time Range',
            id: 'time-range',
            hasErrors: false,
          },
          {
            label: 'Filters',
            id: 'filters',
            hasErrors: this.childErrors('filters', this.errors).length > 0,
          },
          {
            label: 'Table Styles',
            id: 'table-styles',
            hasErrors: this.childErrors('tracker_table_style', this.errors).length > 0,
          },
          {
            label: 'Time Dimension',
            id: 'time-dimension',
            hasErrors: this.childErrors('time_dimension', this.errors).length > 0,
          },
          {
            label: 'Pivot Rows',
            id: 'pivot-rows',
            hasErrors: this.childErrors('tracker_pivot', this.errors).length > 0,
          },
          {
            label: 'Total Column Headers',
            id: 'total-column-headers',
            hasErrors: this.childErrors('tracker_total_headers', this.errors).length > 0,
          },
        ]
      },
    },
    mounted () {},

    beforeDestroy () {},

    methods: {
      onScroll (event) {
        if (this._scrollingToSection) return
        const container = event.target
        const scrollTop = container.scrollTop
        const scrollingDown = scrollTop >= (this._lastScrollTop || 0)
        this._lastScrollTop = scrollTop
        const scrollableDivRect = container.getBoundingClientRect()
        const threshold = 10
        let activeSection = this.tabs[0].id
        for (const tab of this.tabs) {
          const el = container.querySelector(`#${tab.id}`)
          if (!el) continue
          if (el.getBoundingClientRect().top <= scrollableDivRect.top + threshold) {
            activeSection = tab.id
          }
        }
        const currentIndex = this.tabs.findIndex(t => t.id === this.inputSectionTab)
        const newIndex = this.tabs.findIndex(t => t.id === activeSection)
        if (scrollingDown && newIndex >= currentIndex) {
          this.inputSectionTab = activeSection
        } else if (!scrollingDown && newIndex <= currentIndex) {
          this.inputSectionTab = activeSection
        }
      },
      scrollToSection (sectionId) {
        this._scrollingToSection = true
        this.inputSectionTab = sectionId
        const container = this.$el.querySelector('.editor')
        const el = container && container.querySelector(`#${sectionId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', })
        }
        clearTimeout(this._scrollTimer)
        this._scrollTimer = setTimeout(() => { this._scrollingToSection = false }, 400)
      },
      childErrors: _childErrors,
      removeReport (key) {
        this.$emit('remove', key)
      },

      tabSelection (tab) {
        this.inputSectionTab = tab
      },

      showModulePicker () {},

      makeInputsDirty () {},

      selectModule () {},

      openTrackerTableModule () {},

    // showModulePicker () {

    // },
    },
  }
</script>

<style lang="scss" scoped>
.table-editor-container {
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.widget-type {
  display: flex;
  font-weight: 400;
  font-size: 15px;
  align-items: center;
  img {
    width: 35px;
    height: 35px;
    margin-right: 10px;
    border: $border-1;
    border-radius: 8px;
    padding: 5px;
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.editor {
  padding: 20px;
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  flex-direction: column;
  background: #fbfbfb;

  .editor-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h2 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 10px;
      text-transform: capitalize;
    }

    .body {
      border: $border-1;
      border-radius: 10px;
      padding: 18px;
      background: white;
    }
  }
}

.editor-header {
  border-bottom: $border-1;
  padding: 20px;

  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 18px;
    align-items: center;
    margin-bottom: 15px;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}
</style>
